import {
	ApiInstitution,
	ApiInstitutionActivityResponse,
	ApiInstitutionBranch,
	ApiInstitutionBranchItem,
	ApiInstitutionPerson,
	ApiInstitutionReduced,
	ApiUpdateInstitutionPerson,
	CreateInstitutionApiBody,
	CreateInstitutionPersonApiBody,
} from '../api-types/institution.api';
import { ApiNeighborContext } from '../api-types/neighbor.api';
import {
	CreateInstitution,
	CreateInstitutionPerson,
	Institution,
	InstitutionActivityResponse,
	InstitutionBranch,
	InstitutionBranchItem,
	InstitutionExtended,
	InstitutionPerson,
	UpdateInstitutionPerson,
} from '../app-types/institution.app';
import { NeighborContext } from '../app-types/neighbors.app';
import { transformApiAddressToAppAddress, transformAppCreateAddressToApiCreateAddressBody } from './address.transform';
import { transformApiCityDistrictToAppCityDistrict } from './city-district.transform';
import { transformApiContactPointsToAppContactPoints } from './contact-point.transform';
import {
	transformApiWorkplaceJobToAppWorkplaceJob,
	transformAppWorkplaceJobToApiWorkplaceJob,
} from './workplace-transform';

export function transformApiInstitutionActivityToInstitutionActivity(
	response: ApiInstitutionActivityResponse,
): InstitutionActivityResponse {
	return {
		activeClientCount: response.activeClientCount,
		activeProfessionals: response.activeProfessionals.map((professional) => ({
			userId: professional.objectId,
			firstName: professional.firstName,
			lastName: professional.lastName,
			integrationServiceOfficeId: professional.integrationServiceOfficeId,
			integrationServiceOfficeName: professional.integrationServiceOfficeName,
		})),
	};
}

/**
 * Converts what we internally use for creating a new institution to an object that is understood
 * by the API.
 *
 * @param input - The CreateInstitution object containing institution details.
 * @param categories - The list of ContactPointCategory2 to map communication ways.
 * @returns The transformed CreateInstitutionApiBody ready for API consumption.
 */
export const tranformCreateInstitutionToApiPayload = (input: CreateInstitution): CreateInstitutionApiBody => {
	const body: CreateInstitutionApiBody = {
		address: {
			anonym: '',
			postalCodeAndCity: input.address?.postalCodeAndCity ?? '',
			streetAndHouseNumber: input.address?.streetAndHouseNumber ?? '',
			soundexStreet: '',
		},
		name: input.name,
		additionalName: input.additionalName,
		isRequiredToEmploy: input.employmentRequired,
		schoolNumber: input.schoolNumber,
		url: input.url,
		industryName: input.industryName,
		contactPoints: (input.contactPoints || []).map((cp) => ({
			remark: cp.remark,
			categoryContactPointId: cp.categoryContactPointId,
			contactValue: cp.contactValue,
			preferredWayOfContact: cp.preferredWayOfContact,
		})),
	};
	return body;
};

/**
 * Transforms an API institution object to the application's Institution format.
 * @param input - The ApiInstitutionReduced object from the API.
 * @returns The transformed Institution object for the application.
 */
export const transformApiInstitutionToAppInstitution = (input: ApiInstitutionReduced): Institution => {
	return {
		id: input.id,
		name: input.name,
		category: input.category ?? input.industryName ?? undefined,
		schoolNumber: input.schoolNumber,
		additionalName: input.additionalName,
		address: input.address ? transformApiAddressToAppAddress(input.address) : undefined,
		employmentRequired: input.isRequiredToEmploy,
		industryName: input.industryName,
		url: input.url,
		phone: input.phone ?? undefined,
		email: input.email ?? undefined,
		contactPersons: input.contactPersons ?? undefined,
		activeClients: input.activeClients ?? undefined,
		workplaces: [],
	};
};

/**
 * Transforms an API institution branch object to the application's InstitutionBranch format.
 * @param input API version of the institution branch
 * @returns The transformed InstitutionBranch object for the application.
 */
export const transformApiInstitutionBranchToAppInstitutionBranch = (input: ApiInstitutionBranch) => {
	const output: InstitutionBranch = {
		id: input.id,
		name: input.name,
		schoolNumber: input.schoolNumber,
		careerOrientationStartDate: new Date(input.careerOrientationStartDate),
		cityDistrict: transformApiCityDistrictToAppCityDistrict(input.cityDistrict),
		address: input.institutionBranchAddress
			? transformApiAddressToAppAddress(input.institutionBranchAddress)
			: undefined,
		workplaceJobs: (input.workplacesJobs || []).map(transformApiWorkplaceJobToAppWorkplaceJob),
		isMainInstitution: input.isMainInstitution,
		responsiblePersons: input.institutionPersons
			? transformApiInstitutionPersonsToAppInstitutionPersons(input.institutionPersons)
			: [],
		institutionId: input.institutionId,
	};
	return output;
};

/**
 * Transforms an internal version of the institution branch to the API version.
 * @param input internal version of the institution branch
 * @returns The API version of the institution branch
 */
export const transformAppInstitutionBranchToApiInstitutionBranch = (input: InstitutionBranch) => {
	const output: ApiInstitutionBranch = {
		id: input.id,
		name: input.name,
		schoolNumber: input.schoolNumber,
		careerOrientationStartDate: input.careerOrientationStartDate.toISOString(),
		cityDistrict: input.cityDistrict,
		institutionBranchAddress: input.address ? transformApiAddressToAppAddress(input.address) : undefined,
		workplacesJobs: (input.workplaceJobs || []).map(transformAppWorkplaceJobToApiWorkplaceJob),
		isMainInstitution: input.isMainInstitution,
		institutionPersons: input.responsiblePersons
			? transformAppInstitutionPersonsToApiInstitutionPersons(input.responsiblePersons)
			: [],
	};
	return output;
};

/**
 * Transforms an array of API institution branch objects to the application's InstitutionBranch format.
 * @param input - The array of ApiInstitutionBranch objects from the API.
 * @returns The transformed array of InstitutionBranch objects for the application.
 */
export const transformApiInstitutionBranchesToAppInstitutionBranches = (input: ApiInstitutionBranch[]) => {
	const output: InstitutionBranch[] = input.map(transformApiInstitutionBranchToAppInstitutionBranch);
	return output;
};

/**
 * Transforms an array of API institution objects to the application's Institution format.
 * @param input - The array of ApiInstitutionReduced objects from the API.
 * @returns The transformed array of Institution objects for the application.
 */
export const transformApiInstitutionsToAppInstitutions = (input: ApiInstitutionReduced[]): Institution[] => {
	return input.map(transformApiInstitutionToAppInstitution);
};

export const transformApiInstitutionExtendedToInstitutionExtended = (input: ApiInstitution): InstitutionExtended => {
	const institutionBranches = transformApiInstitutionBranchesToAppInstitutionBranches(input.institutionBranches || []);
	const schoolNumberFromBranch = institutionBranches.find(
		(branch) => !!branch.schoolNumber && branch.isMainInstitution,
	)?.schoolNumber;
	const base = transformApiInstitutionToAppInstitution(input);

	return {
		...base,
		schoolNumber: schoolNumberFromBranch,
		institutionBranches,
		contactPoints: transformApiContactPointsToAppContactPoints(input.contactPoints),
	};
};

export const transformInstitutionExtendedToApiInstitutionExtended = (input: InstitutionExtended): ApiInstitution => {
	return {
		id: input.id,
		name: input.name,
		additionalName: input.additionalName,
		industryName: input.industryName,
		isRequiredToEmploy: input.employmentRequired,
		address: input.address ? transformApiAddressToAppAddress(input.address) : undefined,
		url: input.url,
		institutionPersons: [],
		institutionBranches: [],
		schoolNumber: input.schoolNumber,
		contactPoints: input.contactPoints.map((cp) => ({
			id: cp.id!,
			categoryContactPointId: cp.categoryContactPointId,
			contactValue: cp.contactValue,
			preferredWayOfContact: cp.preferredWayOfContact,
			country: undefined,
			remark: cp.remark,
			personId: cp.personId,
			personTypeId: cp.personTypeId,
		})),
	};
};

/**
 * Transform the data which is internally used for creating a new institution person
 * to an object which is understood by the API.
 * @param input internal data structure
 * @returns API compatible data structure
 */
export const transformCreateInstitutionPersonToApiBody = (
	input: CreateInstitutionPerson,
): CreateInstitutionPersonApiBody => {
	return {
		firstName: input.firstName,
		title: input.title,
		categoryPersonTypeId: input.categoryId,
		remark: input.remark,
		genderId: input.genderId,
		address: transformAppCreateAddressToApiCreateAddressBody(input.address),
		contactPoints: (input.communicationWays || []).map((cp) => ({
			remark: cp.remark,
			categoryContactPointId: cp.categoryContactPointId,
			contactValue: cp.contactValue,
			preferredWayOfContact: cp.preferredWayOfContact,
		})),
		dateOfBirth: input.dateOfBirth,
		birthName: input.birthName,
		department: input.department,
		role: input.role,
		name: input.lastName,
		soundex: input.soundex,
		otherText: input.otherText,
		deactivationReason: input.deactivationReason,
		gender: input.genderId
			? {
					id: input.genderId,
					initials: '',
					salutation: '',
					salutationSuggestion: '',
				}
			: undefined,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt,
		typeOfClarification: input.typeOfClarification,
		institutionBranchIds: input.responsibleBranchIds,
	};
};

export const transformApiInstitutionPersonToAppInstitutionPerson = (input: ApiInstitutionPerson) => {
	const output: InstitutionPerson = {
		id: input.id,
		address: transformApiAddressToAppAddress(input.address),
		personType: input.categoryPersonType,
		contactPoints: transformApiContactPointsToAppContactPoints(input.contactPoint || []),
		firstName: input.firstName,
		gender: input.gender,
		genderId: input.genderId,
		name: input.name,
		birthName: input.birthName,
		dateOfBirth: input.dateOfBirth,
		deactivationReason: input.deactivationReason,
		department: input.department,
		otherText: input.otherText,
		remark: input.remark,
		role: input.role,
		soundex: input.soundex,
		title: input.title,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt ? new Date(input.clarifiedAt) : undefined,
		typeOfClarification: input.typeOfClarification,
		isDeactivated: input.isDeactivated,
		responsibleBranches: input.institutionBranches,
	};
	return output;
};

export const transformAppInstitutionPersonToApiInstitutionPerson = (input: InstitutionPerson) => {
	const output: ApiInstitutionPerson = {
		id: input.id,
		hasBeenClarified: input.hasBeenClarified,
		typeOfClarification: input.typeOfClarification,
		address: transformApiAddressToAppAddress(input.address),
		categoryPersonType: input.personType,
		contactPoint: (input.contactPoints || []).map((cp) => ({
			id: cp.id!,
			categoryContactPointId: cp.categoryContactPointId,
			contactValue: cp.contactValue,
			preferredWayOfContact: cp.preferredWayOfContact,
			country: undefined,
			remark: cp.remark,
			personId: cp.personId,
			personTypeId: cp.personTypeId,
		})),
		firstName: input.firstName,
		gender: input.gender,
		genderId: input.genderId,
		name: input.name,
		birthName: input.birthName,
		dateOfBirth: input.dateOfBirth,
		deactivationReason: input.deactivationReason,
		department: input.department,
		otherText: input.otherText,
		remark: input.remark,
		role: input.role,
		soundex: input.soundex,
		title: input.title,
		isDeactivated: input.isDeactivated,
	};
	return output;
};

export const transformAppInstitutionPersonsToApiInstitutionPersons = (input: InstitutionPerson[]) => {
	const output: ApiInstitutionPerson[] = input.map(transformAppInstitutionPersonToApiInstitutionPerson);
	return output;
};

export const transformApiInstitutionPersonsToAppInstitutionPersons = (input: ApiInstitutionPerson[]) => {
	const output: InstitutionPerson[] = input.map(transformApiInstitutionPersonToAppInstitutionPerson);
	return output;
};

export const transformAppUpdateInstitutionPersonToApiUpdateInstitutionPerson = (input: UpdateInstitutionPerson) => {
	const output: ApiUpdateInstitutionPerson = {
		address: input.address ? transformApiAddressToAppAddress(input.address) : undefined,
		categoryPersonTypeId: input.categoryId,
		contactPoints: (input.communicationWays || []).map((cp) => ({
			remark: cp.remark,
			categoryContactPointId: cp.categoryContactPointId,
			contactValue: cp.contactValue,
			preferredWayOfContact: cp.preferredWayOfContact,
		})),
		title: input.title,
		firstName: input.firstName,
		name: input.name,
		birthName: input.birthName,
		genderId: input.genderId,
		gender: input.genderId
			? {
					id: input.genderId,
					initials: '',
					salutation: '',
					salutationSuggestion: '',
				}
			: undefined,
		role: input.role,
		soundex: input.soundex,
		hasBeenClarified: input.hasBeenClarified,
		typeOfClarification: input.typeOfClarification,
		clarifiedAt: input.clarifiedAt,
		remark: input.remark,
		dateOfBirth: input.dateOfBirth,
		deactivationReason: input.deactivationReason,
		department: input.department,
		otherText: input.otherText,
		institutionBranchIds: input.responsibleBranchIds,
	};
	return output;
};

export function transformApiInstitutionPersonNeighborsToAppInstitutionPersonNeighbors(
	input: ApiNeighborContext<ApiInstitutionPerson>,
): NeighborContext<InstitutionPerson> {
	return {
		current: transformApiInstitutionPersonToAppInstitutionPerson(input.current),
		previous: input.previous ? transformApiInstitutionPersonToAppInstitutionPerson(input.previous) : null,
		next: input.next ? transformApiInstitutionPersonToAppInstitutionPerson(input.next) : null,
	};
}

export function transformApiInstitutionBranchItemToAppInstitutionBranchItem(
	input: ApiInstitutionBranchItem,
): InstitutionBranchItem {
	return {
		id: input.id,
		name: input.name,
		isMainInstitution: input.isMainInstitution,
	};
}
